import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Edit, Trash2, Image, ExternalLink, MoveUp, MoveDown, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const SlideManager = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image_url: "",
    link_url: "",
    link_type: "external",
    category: "News",
    order: 0
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/admin/slides`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlides(response.data);
    } catch (error) {
      toast.error("Error mengambil slides");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('auth_token');
      
      if (selectedSlide) {
        await axios.put(`${API}/admin/slides/${selectedSlide.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Slide berhasil diperbarui!");
      } else {
        await axios.post(`${API}/admin/slides`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Slide berhasil dibuat!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchSlides();
    } catch (error) {
      toast.error("Error menyimpan slide: " + (error.response?.data?.detail || "Unknown error"));
    }
  };

  const handleDelete = async (slideId) => {
    if (!confirm("Apakah Anda yakin ingin menghapus slide ini?")) return;

    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API}/admin/slides/${slideId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Slide berhasil dihapus!");
      fetchSlides();
    } catch (error) {
      toast.error("Error menghapus slide");
    }
  };

  const openEditDialog = (slide) => {
    setSelectedSlide(slide);
    setFormData({
      title: slide.title,
      description: slide.description || "",
      image_url: slide.image_url,
      link_url: slide.link_url || "",
      link_type: slide.link_type || "external",
      category: slide.category || "News",
      order: slide.order
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      image_url: "",
      link_url: "",
      link_type: "external",
      category: "News",
      order: slides.length
    });
    setSelectedSlide(null);
  };

  const moveSlide = async (slideId, direction) => {
    const slideIndex = slides.findIndex(s => s.id === slideId);
    if (slideIndex === -1) return;
    
    const newSlides = [...slides];
    const targetIndex = direction === 'up' ? slideIndex - 1 : slideIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= newSlides.length) return;
    
    // Swap slides
    [newSlides[slideIndex], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[slideIndex]];
    
    // Update order values
    newSlides.forEach((slide, index) => {
      slide.order = index;
    });
    
    try {
      const token = localStorage.getItem('auth_token');
      
      // Update both slides
      await Promise.all([
        axios.put(`${API}/admin/slides/${newSlides[slideIndex].id}`, {...newSlides[slideIndex]}, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.put(`${API}/admin/slides/${newSlides[targetIndex].id}`, {...newSlides[targetIndex]}, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSlides(newSlides);
      toast.success("Urutan slide berhasil diubah!");
    } catch (error) {
      toast.error("Error mengubah urutan slide");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-800">Kelola Slides</h2>
        <div className="text-center py-8">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Kelola Slides</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Tambah Slide Baru
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedSlide ? "Edit Slide" : "Tambah Slide Baru"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Judul Slide</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Judul slide yang menarik"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Deskripsi</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Deskripsi slide (opsional)"
                  rows="3"
                />
              </div>

              <div>
                <Label htmlFor="image_url">URL Gambar</Label>
                <Input
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="https://example.com/image.jpg"
                  required
                />
                {formData.image_url && (
                  <div className="mt-2">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full max-w-sm h-32 object-cover rounded border"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="link_type">Tipe Link</Label>
                <Select value={formData.link_type} onValueChange={(value) => setFormData({...formData, link_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">Link Eksternal</SelectItem>
                    <SelectItem value="article">Artikel Internal</SelectItem>
                    <SelectItem value="internal">Halaman Internal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="link_url">URL Link</Label>
                <Input
                  id="link_url"
                  value={formData.link_url}
                  onChange={(e) => setFormData({...formData, link_url: e.target.value})}
                  placeholder={
                    formData.link_type === 'external' ? "https://example.com" :
                    formData.link_type === 'article' ? "/article/123" :
                    "/page"
                  }
                />
              </div>

              <div>
                <Label htmlFor="category">Kategori</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  placeholder="News, Promo, Tutorial, dll"
                />
              </div>

              <div>
                <Label htmlFor="order">Urutan</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">Slide dengan urutan lebih kecil akan ditampilkan lebih dulu</p>
              </div>

              <Button type="submit" className="w-full">
                {selectedSlide ? "Update Slide" : "Simpan Slide"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Slides List */}
      <div className="space-y-4">
        {slides.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Belum ada slide. Buat slide pertama Anda!</p>
            </CardContent>
          </Card>
        ) : (
          slides.map((slide, index) => (
            <Card key={slide.id} className="shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <img
                    src={slide.image_url}
                    alt={slide.title}
                    className="w-32 h-20 object-cover rounded"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/200/120';
                    }}
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {slide.title}
                        </h3>
                        {slide.description && (
                          <p className="text-gray-600 text-sm mb-2">
                            {slide.description}
                          </p>
                        )}
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <Badge variant="secondary">{slide.category}</Badge>
                          <span>Urutan: {slide.order}</span>
                          <div className="flex items-center">
                            {slide.is_active ? (
                              <><Eye className="w-4 h-4 mr-1" /> Aktif</>
                            ) : (
                              <><EyeOff className="w-4 h-4 mr-1" /> Nonaktif</>
                            )}
                          </div>
                          {slide.link_url && (
                            <a 
                              href={slide.link_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-blue-600 hover:text-blue-800"
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Link
                            </a>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {/* Move buttons */}
                        <div className="flex flex-col space-y-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => moveSlide(slide.id, 'up')}
                            disabled={index === 0}
                            className="h-6 w-6 p-0"
                          >
                            <MoveUp className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => moveSlide(slide.id, 'down')}
                            disabled={index === slides.length - 1}
                            className="h-6 w-6 p-0"
                          >
                            <MoveDown className="w-3 h-3" />
                          </Button>
                        </div>
                        
                        {/* Edit and Delete buttons */}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEditDialog(slide)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDelete(slide.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Preview Note */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs font-bold">i</span>
            </div>
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Informasi Slide</h4>
              <p className="text-blue-800 text-sm">
                Slide akan ditampilkan di halaman utama sebagai hero banner. 
                Slide dengan urutan terkecil akan ditampilkan pertama. 
                Slide akan berganti otomatis setiap 5 detik.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SlideManager;